import { Component, OnInit } from '@angular/core';
import { DataserviceService } from '../dataservice.service';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-marketing-management',
  templateUrl: './marketing-management.component.html',
  styleUrl: './marketing-management.component.css'
})
export class MarketingManagementComponent implements OnInit {
  currentUser: any;
  buckets: any[] = [];
  loading = false;
  editModalVisible = false;
  editModalTitle = '';
  editModalContent = '';
  editModalFooter = '';
  createModalVisible = false;
  createModalTitle = '';
  createModalContent = '';
  createModalFooter = '';

  statusOptions: string[] = ['active', 'inactive'];

  selectedBucket: any = null;
  createForm: FormGroup;
  editForm: FormGroup;

  constructor(
    private data: DataserviceService,
    private fb: FormBuilder,
    private nzNotify: NzNotificationService
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);

    this.createForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      status: ['active', Validators.required],
      placeAfter: ['end']
    });

    this.editForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      status: ['', Validators.required],
      placeAfter: [null]
    });
  }

  ngOnInit() {
    this.getBuckets();
  }

  async getBuckets() {
    const companyId = this.currentUser?.company_id;
    if (!companyId) {
      this.buckets = [];
      return;
    }
    const res = await firstValueFrom(
      this.data.getData(`market-mgmt/buckets/company/${companyId}`)
    );
    const data = res?.data ?? [];
    this.buckets = (Array.isArray(data) ? data : []).slice().sort((a: any, b: any) => {
      const pa = parseInt(a.priority, 10) || 0;
      const pb = parseInt(b.priority, 10) || 0;
      return pa - pb;
    });
  }

  async createBucket(body: any) {
    const obj = {
      name: body.name,
      description: body.description ?? '',
      status: body.status,
      company_id: this.currentUser.company_id,
      created_by: this.currentUser.id,
      insert_after_id: body.placeAfter ?? 'end'
    };
    const res = await firstValueFrom(
      this.data.postData(obj, 'market-mgmt/buckets/create')
    );
    if (res?.result === 'success') {
      this.nzNotify.success('Bucket created', 'Bucket created successfully');
      this.createModalVisible = false;
      this.createForm.reset();
    } else {
      this.nzNotify.error('Create failed', res?.message || 'Failed to create bucket');
    }
    this.getBuckets();
  }

  openCreateModal() {
    this.createModalVisible = true;
    this.createModalTitle = 'Create Bucket';
    this.createModalContent = 'Create Bucket';
    this.createModalFooter = 'Create Bucket';
    this.selectedBucket = null;
    this.createForm.patchValue({
      name: '',
      description: '',
      status: 'active',
      placeAfter: 'end'
    });
  }

  get positionOptions(): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [
      { value: 'start', label: 'At beginning' }
    ];
    (this.buckets || []).forEach((b: any) => {
      options.push({ value: b.id, label: `After ${b.name || 'Unnamed'}` });
    });
    options.push({ value: 'end', label: 'At end' });
    return options;
  }

  get positionOptionsForEdit(): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [
      { value: 'start', label: 'At beginning' }
    ];
    (this.buckets || []).forEach((b: any) => {
      if (b.id !== this.selectedBucket?.id) {
        options.push({ value: b.id, label: `After ${b.name || 'Unnamed'}` });
      }
    });
    options.push({ value: 'end', label: 'At end' });
    return options;
  }

  async deleteBucket(id: string) {
    const res = await firstValueFrom(
      this.data.postDataNoBodyDotBody(
        { deleted_by: this.currentUser?.id },
        `market-mgmt/buckets/delete/${id}`
      )
    );
    if (res?.result === 'success') {
      this.nzNotify.success('Bucket deleted', 'Bucket deleted successfully');
    } else {
      this.nzNotify.error('Delete failed', res?.message || 'Failed to delete bucket');
    }
    this.getBuckets();
  }

  openEditModal(id: string) {
    this.editModalVisible = true;
    this.editModalTitle = 'Edit Bucket';
    this.editModalContent = 'Edit Bucket';
    this.editModalFooter = 'Edit Bucket';
    this.selectedBucket = this.buckets.find((b: any) => b.id === id) ?? null;
    this.editForm.patchValue({
      name: this.selectedBucket?.name ?? '',
      description: this.selectedBucket?.description ?? '',
      status: this.selectedBucket?.status ?? 'active',
      placeAfter: this.getPlaceAfterForBucket(this.selectedBucket)
    });
  }

  getPlaceAfterForBucket(bucket: any): string {
    if (!bucket) return 'start';
    const idx = this.buckets.findIndex((b: any) => b.id === bucket.id);
    if (idx <= 0) return 'start';
    return this.buckets[idx - 1].id;
  }

  async editBucket(id: string, body: any) {
    const companyId = this.selectedBucket?.company?.id ?? this.selectedBucket?.company_id ?? this.currentUser?.company_id;
    const payload = {
      name: body.name,
      description: body.description ?? '',
      status: body.status,
      updated_by: this.currentUser.id,
      company_id: companyId,
      move_to_after_id: body.placeAfter ?? 'end'
    };
    const res = await firstValueFrom(
      this.data.postData(payload, `market-mgmt/buckets/update/${id}`)
    );
    if (res?.result === 'success') {
      this.nzNotify.success('Bucket updated', 'Bucket updated successfully');
      this.editModalVisible = false;
      this.editForm.reset();
    } else {
      this.nzNotify.error('Update failed', res?.message || 'Failed to update bucket');
    }
    this.getBuckets();
  }
}